import type {
  ApiVersion,
  AwsConnectionSettings,
  AzureConnectionSettings,
  CiscoFmcDataErrorHandlingSettings,
  CiscoFmcSessionSettings,
  CombinedDiscovery,
  NsxTServerSettings,
  PaginationSettings,
  RateLimitationSettings,
  RateLimitationSettingsWithMultipleRateLimiters,
  RateLimiterId,
  RateLimiterSettings,
  SilverPeakBroadcastCliSettings,
  SilverPeakLoginType,
  VendorApiHttpAdvancedSettings,
  VendorApiHttpAgentSettings,
  VendorApiSessionSettings,
  VersaDiscoverySettings,
  ViptelaDataErrorHandlingSettings,
  ViptelaDiscoverySettings,
  ViptelaSessionSettings,
} from '../types';
import type { WebsocketParsingOptions, WebsocketTimeouts } from '../types/ws';

export interface BaseVendorApiSettingsDefaults {
  clientStatsIntervalSec: number;
}

export interface HttpAgentSettingsDefaults
  extends Required<VendorApiHttpAgentSettings> {}

export interface PaginationSettingsDefaults
  extends Required<PaginationSettings> {}

export interface VendorApiHttpAdvancedSettingsDefaults
  extends Required<VendorApiHttpAdvancedSettings> {}

export interface SessionSettingsDefaults
  extends Required<VendorApiSessionSettings> {}

export interface BaseHttpBasedVendorApiSettingsDefaults
  extends BaseVendorApiSettingsDefaults,
    Required<RateLimitationSettings>,
    HttpAgentSettingsDefaults,
    VendorApiHttpAdvancedSettingsDefaults {}

export interface BaseHttpBasedVendorApiSettingsWithMultipleRateLimitersDefaults<
  TRateLimiterId extends string,
> extends BaseVendorApiSettingsDefaults,
    Required<RateLimitationSettingsWithMultipleRateLimiters<never>>,
    VendorApiHttpAdvancedSettingsDefaults {
  rateLimiters: {
    [id in RateLimiterId<TRateLimiterId>]: Required<RateLimiterSettings>;
  };
}

export interface AwsApiSettingsDefaults
  extends BaseVendorApiSettingsDefaults,
    Required<RateLimitationSettings>,
    Required<AwsConnectionSettings>,
    Required<ApiVersion> {}

export interface AzureApiSettingsDefaults
  extends HttpBasedSessionVendorApiSettingsDefaults,
    Required<AzureConnectionSettings> {}

export interface HttpBasedSessionVendorApiSettingsDefaults
  extends BaseHttpBasedVendorApiSettingsDefaults,
    SessionSettingsDefaults {}

export interface ArubaNewCentralApiSettingsDefaults
  extends HttpBasedSessionVendorApiSettingsDefaults {}

export interface WebsocketParsingOptionsDefaults
  extends Required<WebsocketParsingOptions> {}

export interface WebsocketTimeoutsDefaults
  extends Required<WebsocketTimeouts> {}

export interface CiscoApicApiSettingsDefaults
  extends HttpBasedSessionVendorApiSettingsDefaults,
    PaginationSettingsDefaults {}

export interface CiscoFmcApiSettingsDefaults
  extends BaseHttpBasedVendorApiSettingsDefaults,
    Required<CiscoFmcSessionSettings>,
    PaginationSettingsDefaults,
    Required<CiscoFmcDataErrorHandlingSettings> {}

export interface BaseF5osApiSettingsDefaults
  extends BaseHttpBasedVendorApiSettingsDefaults,
    Required<VendorApiSessionSettings> {}

export interface F5osAApiSettingsDefaults extends BaseF5osApiSettingsDefaults {}

export interface F5osCApiSettingsDefaults extends BaseF5osApiSettingsDefaults {}

export interface JuniperMistApiSettingsDefaults
  extends BaseHttpBasedVendorApiSettingsDefaults,
    PaginationSettingsDefaults {}

export interface MerakiApiSettingsDefaults
  extends BaseHttpBasedVendorApiSettingsDefaults,
    PaginationSettingsDefaults {}

export interface NsxTApiSettingsDefaults
  extends HttpBasedSessionVendorApiSettingsDefaults,
    Required<NsxTServerSettings> {}

export interface RuckusVszApiSettingsDefaults
  extends HttpBasedSessionVendorApiSettingsDefaults,
    PaginationSettingsDefaults {}

export interface SilverPeakApiSettingsDefaults
  extends HttpBasedSessionVendorApiSettingsDefaults,
    Required<SilverPeakBroadcastCliSettings> {
  loginType: SilverPeakLoginType['loginType'];
}

export interface VersaApiSettingsDefaults
  extends BaseHttpBasedVendorApiSettingsDefaults,
    Required<CombinedDiscovery>,
    Required<VersaDiscoverySettings> {}

export interface ViptelaApiSettingsDefaults
  extends BaseHttpBasedVendorApiSettingsDefaults,
    Required<CombinedDiscovery>,
    Required<ViptelaSessionSettings>,
    Required<ViptelaDataErrorHandlingSettings>,
    Required<ViptelaDiscoverySettings> {}

export interface VeloCloudApiSettingsDefaults
  extends HttpBasedSessionVendorApiSettingsDefaults,
    WebsocketTimeoutsDefaults,
    WebsocketParsingOptionsDefaults {
  veloCloudWsCommandMaxAttempts: number;
  veloCloudWsCommandRetryDelayMs: number;
}
